import { supabase } from '@/lib/supabase';
import { CONTRACTS_BUCKET, LOGOS_BUCKET, removeStorageFile, uploadPrivateFile, uploadPublicFile } from '@/lib/storage';
import type { Project, ProjectStatus } from '@/types';

export interface SaveProjectInput {
  id?: string;
  name: string;
  description: string | null;
  status: ProjectStatus;
  billing_day: number | null;
  monthly_amount: number | null;
  development_start: string | null;
  development_end: string | null;
  contract_start: string | null;
  contract_end: string | null;
  link_proyecto: string | null;
  clientName: string;
  clientPhone: string;
  logoFile: File | null;
  contractFile: File | null;
  currentLogoUrl: string | null;
  currentContractUrl: string | null;
  removeLogo: boolean;
  removeContract: boolean;
}

export interface ProjectRelations {
  receipts: number;
  movements: number;
}

/** Busca o crea el cliente y devuelve su identificador. */
async function resolveClientId(name: string, phone: string): Promise<string | null> {
  const cleanName = name.trim();
  if (!cleanName) return null;

  const { data: found } = await supabase
    .from('clients')
    .select('id')
    .ilike('name', cleanName)
    .limit(1)
    .maybeSingle();

  if (found?.id) {
    await supabase
      .from('clients')
      .update({ contact_phone: phone.trim() || null })
      .eq('id', found.id);
    return found.id;
  }

  const { data: created, error } = await supabase
    .from('clients')
    .insert({ name: cleanName, contact_phone: phone.trim() || null })
    .select('id')
    .maybeSingle();

  if (error) throw error;
  return created?.id ?? null;
}

async function registerEvent(projectId: string, eventType: string, description: string) {
  try {
    await supabase.from('project_events').insert({
      project_id: projectId,
      event_type: eventType,
      description,
    });
  } catch {
    // El historial no debe bloquear la operación principal.
  }
}

/** Crea o actualiza un proyecto junto con su cliente, logo y contrato. */
export async function saveProject(input: SaveProjectInput): Promise<Project> {
  const clientId = await resolveClientId(input.clientName, input.clientPhone);

  let logoUrl = input.currentLogoUrl;
  if (input.removeLogo && input.currentLogoUrl) {
    if (!/^https?:\/\//i.test(input.currentLogoUrl)) {
      await removeStorageFile(LOGOS_BUCKET, input.currentLogoUrl);
    }
    logoUrl = null;
  }
  if (input.logoFile) {
    logoUrl = await uploadPublicFile(LOGOS_BUCKET, input.logoFile, 'logos');
  }

  let contractUrl = input.currentContractUrl;
  if (input.removeContract && input.currentContractUrl) {
    if (!/^https?:\/\//i.test(input.currentContractUrl)) {
      await removeStorageFile(CONTRACTS_BUCKET, input.currentContractUrl);
    }
    contractUrl = null;
  }
  if (input.contractFile) {
    contractUrl = await uploadPrivateFile(CONTRACTS_BUCKET, input.contractFile, 'contracts');
  }

  const payload = {
    name: input.name.trim(),
    description: input.description?.trim() || null,
    status: input.status,
    billing_day: input.billing_day,
    monthly_amount: input.monthly_amount,
    development_start: input.development_start,
    development_end: input.development_end,
    contract_start: input.contract_start,
    contract_end: input.contract_end,
    logo_url: logoUrl,
    contract_document_url: contractUrl,
    client_id: clientId,
    link_proyecto: input.link_proyecto,
    updated_at: new Date().toISOString(),
  };

  if (input.id) {
    const { data, error } = await supabase
      .from('projects')
      .update(payload)
      .eq('id', input.id)
      .select('*, clients(*)')
      .maybeSingle();
    if (error) throw error;
    await registerEvent(input.id, 'actualizacion', `Proyecto actualizado: ${payload.name}`);
    return data as Project;
  }

  const { data, error } = await supabase
    .from('projects')
    .insert(payload)
    .select('*, clients(*)')
    .maybeSingle();
  if (error) throw error;
  const project = data as Project;
  await registerEvent(project.id, 'creacion', `Proyecto creado: ${payload.name}`);
  return project;
}

/** Cuenta los registros relacionados que impiden borrar un proyecto de forma segura. */
export async function getProjectRelations(projectId: string): Promise<ProjectRelations> {
  const [receipts, movements] = await Promise.all([
    supabase.from('receipts').select('id', { count: 'exact', head: true }).eq('project_id', projectId),
    supabase.from('financial_movements').select('id', { count: 'exact', head: true }).eq('project_id', projectId),
  ]);
  return {
    receipts: receipts.count ?? 0,
    movements: movements.count ?? 0,
  };
}

/** Elimina un proyecto (solo cuando no tiene recibos asociados). */
export async function deleteProject(project: Project): Promise<void> {
  const { error } = await supabase.from('projects').delete().eq('id', project.id);
  if (error) throw error;
  if (project.logo_url && !/^https?:\/\//i.test(project.logo_url)) {
    await removeStorageFile(LOGOS_BUCKET, project.logo_url);
  }
  if (project.contract_document_url && !/^https?:\/\//i.test(project.contract_document_url)) {
    await removeStorageFile(CONTRACTS_BUCKET, project.contract_document_url);
  }
}